import { create } from "zustand";
import { toast } from "react-toastify";

import { deleteAppStore, getAppStores } from "../services/app.stores.service";
import { getAppCountries } from "../services/app.countries.service";
import { getAppProvinces } from "../services/app.provinces.service";
import { getAppCities } from "../services/app.cities.service";
import { GLOBAL_DEFAULT } from "../constants";
import {
  deleteAppDepartment,
  getAppDepartments,
} from "../services/app.departments.service";
import { getRolesList } from "../services/roles.service";
import {
  deleteStoreAdmin,
  getStoreAdmins,
} from "../services/app.store-admin.service";
import {
  deleteAdminUser,
  getAdminUsers,
} from "../services/admin.users.service";
import {
  deleteAppChannel,
  getAppChannels,
} from "../services/app.channels.service";

const DROPDOWN_CACHE_TTL_MS = 5 * 60 * 1000;
const dropdownCache = new Map<string, { ts: number; data: unknown }>();
const inFlightRequests = new Map<string, Promise<void>>();

function getDropdownCache<T>(key: string): T | null {
  const entry = dropdownCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > DROPDOWN_CACHE_TTL_MS) {
    dropdownCache.delete(key);
    return null;
  }
  return entry.data as T;
}

function setDropdownCache(key: string, data: unknown) {
  dropdownCache.set(key, { ts: Date.now(), data });
}

async function runDeduped(
  key: string,
  fn: () => Promise<void>,
): Promise<void> {
  const existing = inFlightRequests.get(key);
  if (existing) return existing;
  const promise = fn().finally(() => {
    inFlightRequests.delete(key);
  });
  inFlightRequests.set(key, promise);
  return promise;
}

interface DataState {
  loading: boolean;
  totalRecords: number;
  search: string;
  status: string;
  setStatus: (status: string) => void;
  role: string;
  roles: any[];
  setRoles: (roles: any[]) => void;
  fetchRoles: () => Promise<void>;
  currentPage: number;
  rows: number;
  sortOrder: string;
  sortField: string;
  selectedRow: any;
  deleteShow: boolean;
  deleteId: number | undefined;
  setLoading: (loading: boolean) => void;
  setSearch: (search: string) => void;
  setRole: (role: string) => void;
  setCurrentPage: (page: number) => void;
  setRows: (rows: number) => void;
  setSortOrder: (order: string) => void;
  setSortField: (field: string) => void;
  setSelectedRow: (row: any) => void;
  setDeleteShow: (show: boolean) => void;
  setDeleteId: (id: number | undefined) => void;
  storeRecordDelete: (deleteId: any) => Promise<void>;
  departmentRecordDelete: (deleteId: any) => Promise<void>;
  storeAdminRecordDelete: (deleteId: any) => Promise<void>;
  userRecordDelete: (deleteId: any) => Promise<void>;
  channelRecordDelete: (deleteId: any) => Promise<void>;
  storeData: any;
  storesData: any[];
  setStoresData: (storesData: any[]) => void;
  fetchStoresData: () => Promise<void>;
  departmentData: any;
  departmentsData: any[];
  setDepartmentsData: (departmentsData: any[]) => void;
  fetchDepartmentsData: () => Promise<void>;
  storeAdmin: any;
  storeAdmins: any[];
  setStoreAdmins: (storeAdmins: any[]) => void;
  fetchStoreAdmins: () => Promise<void>;
  user: any;
  users: any[];
  setUsers: (users: any[]) => void;
  fetchUsers: () => Promise<void>;
  channel: any;
  channels: any[];
  setChannels: (channels: any[]) => void;
  fetchChannels: () => Promise<void>;
  countries: any[];
  provinces: any[];
  cities: any[];
  stores: any[];
  departments: any[];
  country: any;
  province: any;
  city: any;
  store: any;
  department: any;
  setCountry: (country: any) => void;
  setProvince: (province: any) => void;
  setCity: (city: any) => void;
  setStore: (store: any) => void;
  setDepartment: (department: any) => void;
  setCountries: (countries: any[]) => void;
  setProvinces: (provinces: any[]) => void;
  setCities: (cities: any[]) => void;
  setStores: (stores: any[]) => void;
  setDepartments: (departments: any[]) => void;
  fetchCountries: () => Promise<void>;
  fetchProvinces: (countryId: any) => Promise<void>;
  fetchCities: (provinceId: any) => Promise<void>;
  fetchStores: (cityId?: any) => Promise<void>;
  fetchDepartments: (storeId: any) => Promise<void>;
  reset: () => void;
}

export const useDataStore = create<DataState>((set, get) => ({
  loading: false,
  totalRecords: 0,
  search: "",
  status: "-1",
  role: "-1",
  roles: [],
  currentPage: 1,
  rows: GLOBAL_DEFAULT.ROW_PER_PAGE_10,
  sortOrder: GLOBAL_DEFAULT.SORT_ORDER,
  sortField: GLOBAL_DEFAULT.SORT_FIELD,
  selectedRow: undefined,
  deleteShow: false,
  deleteId: undefined,
  storeData: {},
  storesData: [],
  departmentData: {},
  departmentsData: [],
  storeAdmin: {},
  storeAdmins: [],
  user: {},
  users: [],
  channel: {},
  channels: [],
  countries: [],
  provinces: [],
  cities: [],
  stores: [],
  departments: [],
  country: "-1",
  province: "-1",
  city: "-1",
  store: "-1",
  department: "-1",

  setCountry: (country) =>
    set({ country, province: "-1", city: "-1", store: "-1", department: "-1" }),
  setProvince: (province) =>
    set({ province, city: "-1", store: "-1", department: "-1" }),
  setCity: (city) => set({ city, store: "-1", department: "-1" }),
  setStore: (store) => set({ store, department: "-1" }),
  setDepartment: (department) => set({ department }),
  setCountries: (countries) => set({ countries }),
  setProvinces: (provinces) => set({ provinces }),
  setCities: (cities) => set({ cities }),
  setStores: (stores) => set({ stores }),
  setDepartments: (departments) => set({ departments }),

  setLoading: (loading) => set({ loading }),
  setStatus: (status) => set({ status }),
  setRole: (role) => set({ role }),
  setRoles: (roles) => set({ roles }),
  setSearch: (search) => set({ search, currentPage: 1 }),
  setCurrentPage: (currentPage) => set({ currentPage }),
  setRows: (rows) => set({ rows, currentPage: 1 }),
  setSortOrder: (sortOrder) => set({ sortOrder }),
  setSortField: (sortField) => set({ sortField }),
  setSelectedRow: (selectedRow) => set({ selectedRow }),
  setDeleteShow: (deleteShow) => set({ deleteShow }),
  setDeleteId: (deleteId) => set({ deleteId }),
  setStoresData: (storesData) => set({ storesData }),
  setDepartmentsData: (departmentsData) => set({ departmentsData }),
  setStoreAdmins: (storeAdmins) => set({ storeAdmins }),
  setUsers: (users) => set({ users }),
  setChannels: (channels) => set({ channels }),

  fetchRoles: async () => {
    const cacheKey = "roles-filter";
    const cached = getDropdownCache<any[]>(cacheKey);
    if (cached) {
      set({ roles: cached });
      return;
    }
    return runDeduped("fetchRoles", async () => {
      try {
        const rolesRes = await getRolesList({ page: 1, limit: 100 });
        if (rolesRes.statusCode === 200) {
          const onlyEmployeeManager = rolesRes.data.resultSet
            .filter((r: any) => [3, 4].includes(r.id))
            .map((r: any) => ({ label: r.title, value: r.id }));
          const roles = [
            { label: "All Roles", value: -1 },
            ...onlyEmployeeManager,
          ];
          setDropdownCache(cacheKey, roles);
          set({ roles });
        }
      } catch (error) {
        toast.error("Failed to load dropdown data");
      }
    });
  },

  fetchCountries: async () => {
    const cacheKey = "countries-filter";
    const cached = getDropdownCache<any[]>(cacheKey);
    if (cached) {
      set({ countries: cached });
      return;
    }
    return runDeduped("fetchCountries", async () => {
      try {
        const countriesRes = await getAppCountries({ page: 1, limit: 500 });
        if (countriesRes.statusCode === 200) {
          const countries =
            countriesRes.data?.totalResults > 0
              ? [
                  { label: "All Countries", value: -1 },
                  ...countriesRes.data.resultSet.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                  })),
                ]
              : [];
          setDropdownCache(cacheKey, countries);
          set({ countries });
        }
      } catch (error) {
        toast.error("Failed to load dropdown data");
      }
    });
  },

  fetchProvinces: async (countryId: any) => {
    const cacheKey = `provinces-filter:${countryId}`;
    const cached = getDropdownCache<any[]>(cacheKey);
    if (cached) {
      set({ provinces: cached });
      return;
    }
    return runDeduped(cacheKey, async () => {
      try {
        const provincesRes = await getAppProvinces({
          page: 1,
          limit: 500,
          sort: "Province.name ASC",
          country_id: countryId,
        });
        if (provincesRes.statusCode === 200) {
          const provinces =
            provincesRes.data?.totalResults > 0
              ? [
                  { label: "All Provinces", value: -1 },
                  ...provincesRes.data.resultSet.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                  })),
                ]
              : [];
          setDropdownCache(cacheKey, provinces);
          set({ provinces });
        }
      } catch (error) {
        toast.error("Failed to load provinces");
      }
    });
  },

  fetchCities: async (provinceId: any) => {
    const cacheKey = `cities-filter:${provinceId}`;
    const cached = getDropdownCache<any[]>(cacheKey);
    if (cached) {
      set({ cities: cached });
      return;
    }
    return runDeduped(cacheKey, async () => {
      try {
        const citiesRes = await getAppCities({
          page: 1,
          limit: 500,
          sort: "City.name ASC",
          province_id: provinceId,
        });
        if (citiesRes.statusCode === 200) {
          const cities =
            citiesRes.data?.totalResults > 0
              ? [
                  { label: "All Cities", value: -1 },
                  ...citiesRes.data.resultSet.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                  })),
                ]
              : [];
          setDropdownCache(cacheKey, cities);
          set({ cities });
        }
      } catch (error) {
        toast.error("Failed to load cities");
      }
    });
  },

  fetchStores: async (cityId?: any) => {
    const cacheKey = `stores-filter:${cityId ?? "all"}`;
    const cached = getDropdownCache<any[]>(cacheKey);
    if (cached) {
      set({ stores: cached });
      return;
    }
    return runDeduped(cacheKey, async () => {
      try {
        const storesRes = await getAppStores({
          page: 1,
          limit: 500,
          sort: "Store.name ASC",
          city_id: cityId || undefined,
        });
        if (storesRes.statusCode === 200) {
          const stores =
            storesRes.data?.totalResults > 0
              ? [
                  { label: "All Stores", value: -1 },
                  ...storesRes.data.resultSet.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                  })),
                ]
              : [];
          setDropdownCache(cacheKey, stores);
          set({ stores });
        }
      } catch (error) {
        toast.error("Failed to load stores");
      }
    });
  },

  fetchDepartments: async (storeId: any) => {
    const cacheKey = `departments-filter:${storeId}`;
    const cached = getDropdownCache<any[]>(cacheKey);
    if (cached) {
      set({ departments: cached });
      return;
    }
    return runDeduped(cacheKey, async () => {
      try {
        const departmentsRes = await getAppDepartments({
          page: 1,
          limit: 500,
          sort: "Department.name ASC",
          store: storeId,
        });
        if (departmentsRes.statusCode === 200) {
          const departments =
            departmentsRes.data?.totalResults > 0
              ? [
                  { label: "All Departments", value: -1 },
                  ...departmentsRes.data.resultSet.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                  })),
                ]
              : [];
          setDropdownCache(cacheKey, departments);
          set({ departments });
        }
      } catch (error) {
        toast.error("Failed to load departments");
      }
    });
  },

  fetchStoreAdmins: async () => {
    const {
      currentPage,
      rows,
      search,
      sortField,
      sortOrder,
      country,
      province,
      city,
      store,
    } = get();
    const dedupeKey = `storeAdmins:${currentPage}:${rows}:${search}:${sortField}:${sortOrder}:${country}:${province}:${city}:${store}`;
    return runDeduped(dedupeKey, async () => {
      set({ loading: true });
      try {
      const _sortField =
        sortField === "store"
          ? "store.name"
          : sortField === "department"
            ? "department.name"
            : sortField === "country"
              ? "city.province.country.name"
              : sortField === "province"
                ? "city.province.name"
                : sortField === "city"
                  ? "city.name"
                  : sortField === "name"
                    ? "User.name"
                    : sortField;
      const response = await getStoreAdmins({
        page: currentPage,
        limit: rows,
        search: search,
        sort: _sortField + " " + sortOrder,
        country_id: country !== "-1" ? country : undefined,
        province_id: province !== "-1" ? province : undefined,
        city_id: city !== "-1" ? city : undefined,
        store_id: store !== "-1" ? store : undefined,
      });
      if (response.statusCode === 200) {
        set({
          storeAdmins: response.data.resultSet,
          totalRecords: response.data.totalResults,
        });
      } else {
        toast.error(response.msg || "Failed to load store admins");
      }
      } catch (error) {
        toast.error("Error loading store admins");
      } finally {
        set({ loading: false });
      }
    });
  },

  fetchUsers: async () => {
    const {
      currentPage,
      rows,
      search,
      sortField,
      sortOrder,
      country,
      province,
      city,
      store,
      department,
      role,
    } = get();
    const dedupeKey = `users:${currentPage}:${rows}:${search}:${sortField}:${sortOrder}:${country}:${province}:${city}:${store}:${department}:${role}`;
    return runDeduped(dedupeKey, async () => {
      set({ loading: true });
      try {
      const _sortField =
        sortField === "store"
          ? "store.name"
          : sortField === "department"
            ? "department.name"
            : sortField === "country"
              ? "city.province.country.name"
              : sortField === "province"
                ? "city.province.name"
                : sortField === "city"
                  ? "city.name"
                  : sortField === "name"
                    ? "User.name"
                    : sortField;
      const response = await getAdminUsers({
        page: currentPage,
        limit: rows,
        search: search,
        sort: _sortField + " " + sortOrder,
        country_id: country !== "-1" ? country : undefined,
        province_id: province !== "-1" ? province : undefined,
        city_id: city !== "-1" ? city : undefined,
        store_id: store !== "-1" ? store : undefined,
        department_id: department !== "-1" ? department : undefined,
        role_id: role !== "-1" ? role : -1,
      });
      if (response.statusCode === 200) {
        set({
          users: response.data.resultSet,
          totalRecords: response.data.totalResults,
        });
      } else {
        toast.error(response.msg || "Failed to load users");
      }
      } catch (error) {
        toast.error("Error loading users");
      } finally {
        set({ loading: false });
      }
    });
  },

  fetchChannels: async () => {
    const {
      currentPage,
      rows,
      search,
      sortField,
      sortOrder,
      store,
      department,
      status,
    } = get();
    const dedupeKey = `channels:${currentPage}:${rows}:${search}:${sortField}:${sortOrder}:${store}:${department}:${status}`;
    return runDeduped(dedupeKey, async () => {
      set({ loading: true });
      try {
      const _sortField =
        sortField === "department"
          ? "department.name"
          : sortField === "store"
            ? "store.name"
            : sortField === "channel"
              ? "channel_name"
              : sortField === "status"
                ? "Channel.status"
                : sortField;
      const response = await getAppChannels({
        page: currentPage,
        limit: rows,
        search: search,
        status: status,
        department: department,
        store: store,
        sort: _sortField + " " + sortOrder,
      });
      if (response.statusCode === 200) {
        set({
          channels: response.data.resultSet,
          totalRecords: response.data.totalResults,
        });
      } else {
        toast.error(response.msg || "Failed to load channels");
      }
      } catch (error) {
        toast.error("An error occurred while fetching channels");
      } finally {
        set({ loading: false });
      }
    });
  },

  fetchStoresData: async () => {
    const {
      currentPage,
      rows,
      search,
      sortField,
      sortOrder,
      country,
      province,
      city,
    } = get();
    const dedupeKey = `storesData:${currentPage}:${rows}:${search}:${sortField}:${sortOrder}:${country}:${province}:${city}`;
    return runDeduped(dedupeKey, async () => {
      set({ loading: true });
      try {
      const _sortField = sortField === "name" ? "Store.name" : sortField;
      const response = await getAppStores({
        page: currentPage,
        limit: rows,
        search: search,
        sort: `${_sortField} ${sortOrder}`,
        country_id: country,
        province_id: province,
        city_id: city,
      });
      if (response.statusCode === 200) {
        set({
          storesData: response.data.resultSet,
          totalRecords: response.data.totalResults,
        });
      } else {
        toast.error(response.msg || "Failed to fetch stores");
      }
      } catch (error) {
        toast.error("An error occurred while fetching stores");
      } finally {
        set({ loading: false });
      }
    });
  },

  fetchDepartmentsData: async () => {
    const {
      currentPage,
      rows,
      search,
      sortField,
      sortOrder,
      country,
      province,
      city,
      store,
    } = get();
    const dedupeKey = `departmentsData:${currentPage}:${rows}:${search}:${sortField}:${sortOrder}:${country}:${province}:${city}:${store}`;
    return runDeduped(dedupeKey, async () => {
      set({ loading: true });
      try {
      const _sortField = sortField === "name" ? "Department.name" : sortField;
      const response = await getAppDepartments({
        page: currentPage,
        limit: rows,
        search,
        sort: `${_sortField} ${sortOrder}`,
        country: country,
        province: province,
        city: city,
        store: store,
      });
      if (response.statusCode === 200) {
        set({
          departmentsData: response.data.resultSet,
          totalRecords: response.data.totalResults,
        });
      } else {
        toast.error(response.msg || "Failed to fetch departments");
      }
      } catch (error) {
        toast.error("An error occurred while fetching departments");
      } finally {
        set({ loading: false });
      }
    });
  },

  storeRecordDelete: async (deleteId: any) => {
    set({ loading: true });
    try {
      await deleteAppStore(deleteId);
      toast.success("Store deleted successfully");
      set({ deleteShow: false });
      get().fetchStoresData(); // Re-fetch stores after deletion
    } catch (error) {
      toast.error("An error occurred while deleting the store");
    } finally {
      set({ loading: false });
    }
  },

  departmentRecordDelete: async (deleteId: any) => {
    set({ loading: true });
    try {
      await deleteAppDepartment(deleteId);
      toast.success("Department deleted successfully");
      set({ deleteShow: false });
      get().fetchDepartmentsData(); // Re-fetch stores after deletion
    } catch (error) {
      toast.error("An error occurred while deleting the department");
    } finally {
      set({ loading: false });
    }
  },

  storeAdminRecordDelete: async (deleteId: any) => {
    set({ loading: true });
    try {
      await deleteStoreAdmin(deleteId);
      toast.success("Store deleted successfully");
      set({ deleteShow: false });
      get().fetchStoreAdmins(); // Re-fetch stores after deletion
    } catch (error) {
      toast.error("An error occurred while deleting the store");
    } finally {
      set({ loading: false });
    }
  },

  channelRecordDelete: async (deleteId: any) => {
    set({ loading: true });
    try {
      await deleteAppChannel(deleteId);
      toast.success("Channel deleted successfully");
      set({ deleteShow: false });
      get().fetchChannels(); // Re-fetch channels after deletion
    } catch (error) {
      toast.error("An error occurred while deleting the channel");
    } finally {
      set({ loading: false });
    }
  },

  userRecordDelete: async (deleteId: any) => {
    set({ loading: true });
    try {
      await deleteAdminUser(deleteId);
      toast.success("Store deleted successfully");
      set({ deleteShow: false });
      get().fetchUsers(); // Re-fetch stores after deletion
    } catch (error) {
      toast.error("An error occurred while deleting the store");
    } finally {
      set({ loading: false });
    }
  },

  reset: () =>
    set({
      loading: false,
      totalRecords: 0,
      search: "",
      status: "-1",
      role: "-1",
      roles: [],
      currentPage: 1,
      rows: GLOBAL_DEFAULT.ROW_PER_PAGE_10,
      sortOrder: GLOBAL_DEFAULT.SORT_ORDER,
      sortField: GLOBAL_DEFAULT.SORT_FIELD,
      selectedRow: undefined,
      deleteShow: false,
      deleteId: undefined,
      storeData: {},
      storesData: [],
      departmentData: {},
      departmentsData: [],
      storeAdmin: {},
      storeAdmins: [],
      user: {},
      users: [],
      channel: {},
      channels: [],
      countries: [],
      provinces: [],
      cities: [],
      stores: [],
      departments: [],
      country: "-1",
      province: "-1",
      city: "-1",
      store: "-1",
      department: "-1",
    }),
}));
